/**
 * Zuordnungen — Inventar 2.0, S9 layout correction.
 *
 * S7a replaced the original table with one card per assignment, and the
 * reference alignment then pressed those cards into a three-track CSS grid.
 * That grid is what broke the section: `minmax(0, …)` tracks give a column a
 * share of the row whether or not the content fits, so "MILAN / Zimmer 1" in a
 * ~140px fact cell wrapped letter by letter and the actions crowded the data.
 *
 * A table does not have that failure mode. Auto table layout sizes each column
 * to its content, `whitespace-nowrap` on the short values makes that binding,
 * and the long ones truncate with a title instead of shredding. So the desktop
 * view is a real <table> again — with the columns the section always carried:
 *
 *   Unit / Immobilie · Raum · Menge · Im Einsatz seit · Einsatzzeit ·
 *   Zustand bei Zuweisung · Aktionen
 *
 * ONE MARKUP, NOT TWO — still. The switch is JavaScript (`useIsMobile`, the
 * repo's locked 768px breakpoint, the same call InventoryList.jsx:363 makes),
 * never `hidden md:block`. Mounting both trees would put two „Verschieben" /
 * „Bearbeiten" / „Entfernen" buttons and two unit links in the DOM, and the
 * S6a net queries those with singular global getByRole. H4 pins it.
 *
 * SEMANTICS UNCHANGED. The same fields (unit id, unit short code, address,
 * room, quantity, first_assigned_at, condition — plus the derived
 * Einsatzzeit), the same em-dash fallback for a null room, the same four
 * actions on the same handlers, the same `available <= 0` disable on the add
 * button, and the same echo of `assignErr` behind an open dialog. No payload,
 * no request, no reload touched.
 *
 * Both unit lines keep their own `unit ? … : "—"` ternary: fetchAdminUnits is
 * swallowed with `.catch(() => [])`, so the degraded state is reachable and
 * has to stay legible while the link itself still works off `row.unit_id`.
 */
import React from "react";
import { Link } from "react-router-dom";

import { normalizeUnit } from "../../../api/adminData";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { formatDateOnly } from "../../../utils/datetime";
import { getUnitAddressSubtitle, getUnitHeadingLabel } from "../../../utils/unitDisplayId";
import { DetailCard } from "./InventoryDetailCards";

/**
 * One key/value pair on the PHONE card. The value is the <dd>'s only child, so
 * a lone em dash stays exactly one matchable node — D24 asserts a single "—"
 * per record when room_id is null.
 */
function RowFact({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-[9.5px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={`m-0 mt-0.5 break-words text-[12.5px] font-medium text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

const ACTION =
  "rounded-[6px] border border-border bg-card px-2.5 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const ACTION_DESTRUCTIVE =
  "rounded-[6px] border border-destructive/40 bg-card px-2.5 py-1 text-[11.5px] font-medium text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const TH =
  "whitespace-nowrap px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground";

/** Short tabular values never wrap; that is the whole point of the column. */
const TD = "whitespace-nowrap px-3 py-2.5 align-middle text-[12.5px] font-medium text-foreground";

/** The unit link, its address and its raw id — the widest cell, so the only
 *  one allowed to truncate. Both lower lines keep a title, so nothing the
 *  ellipsis hides becomes unreachable. */
function UnitCell({ row, unit }) {
  const nu = unit ? normalizeUnit(unit) : null;
  const address = nu ? getUnitAddressSubtitle(nu) : "—";
  return (
    <div className="min-w-0 max-w-[280px]">
      <Link
        to={`/admin/units/${encodeURIComponent(row.unit_id)}`}
        className="rounded-[4px] text-[13px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {nu ? getUnitHeadingLabel(nu) : "—"}
      </Link>
      <p
        className="m-0 truncate text-[11.5px] font-medium leading-snug text-muted-foreground"
        title={address}
      >
        {address}
      </p>
      {/* Secondary, but still present: it is part of the visible record today
          and D23 reads it. Monospace and smallest, so it reads as a handle. */}
      <p
        className="m-0 truncate font-mono text-[10px] leading-snug text-muted-foreground"
        title={row.unit_id}
      >
        {row.unit_id}
      </p>
    </div>
  );
}

function ActionButtons({ onReportDamage, onMove, onEdit, onRemove }) {
  return (
    <>
      <button type="button" onClick={onReportDamage} className={ACTION}>
        Schaden melden
      </button>
      <button type="button" onClick={onMove} className={ACTION}>
        Verschieben
      </button>
      <button type="button" onClick={onEdit} className={ACTION}>
        Bearbeiten
      </button>
      <button type="button" onClick={onRemove} className={ACTION_DESTRUCTIVE}>
        Entfernen
      </button>
    </>
  );
}

function AssignmentTable({ assignments, unitById, usageDurationFor, handlers }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className={TH}>Unit / Immobilie</th>
            <th scope="col" className={TH}>Raum</th>
            <th scope="col" className={`${TH} text-right`}>Menge</th>
            <th scope="col" className={TH}>Im Einsatz seit</th>
            <th scope="col" className={TH}>Einsatzzeit</th>
            <th scope="col" className={TH}>Zustand bei Zuweisung</th>
            <th scope="col" className={TH}>
              <span className="sr-only">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {assignments.map((row) => {
            const usage = usageDurationFor(row);
            return (
              <tr
                key={row.id}
                className="border-b border-border transition-colors last:border-b-0 hover:bg-muted/50"
              >
                <td className="px-3 py-2.5 align-middle">
                  <UnitCell row={row} unit={unitById.get(String(row.unit_id))} />
                </td>
                <td className={TD}>{row.room_name || "—"}</td>
                <td className={`${TD} text-right font-mono tabular-nums`}>{row.quantity}</td>
                <td className={TD}>
                  {row.first_assigned_at ? formatDateOnly(row.first_assigned_at) : "—"}
                </td>
                <td className={TD}>{row.first_assigned_at && usage ? usage : "—"}</td>
                <td className={TD}>{row.condition_at_assignment || "—"}</td>
                {/* The action zone: its own column, right-aligned, on one line.
                    It can no longer collide with the data because the table
                    reserves exactly the width these four buttons need. */}
                <td className="whitespace-nowrap px-3 py-2.5 text-right align-middle">
                  <div className="inline-flex items-center gap-1.5">
                    <ActionButtons
                      onReportDamage={() => handlers.onReportDamage(row)}
                      onMove={() => handlers.onMove(row)}
                      onEdit={() => handlers.onEdit(row)}
                      onRemove={() => handlers.onRemove(row)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AssignmentCards({ assignments, unitById, usageDurationFor, handlers }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {assignments.map((row) => {
        const usageDurationLabel = usageDurationFor(row);
        return (
          <li
            key={row.id}
            className="rounded-[10px] border border-border bg-muted/40 p-3 transition-colors hover:bg-muted"
          >
            <UnitCell row={row} unit={unitById.get(String(row.unit_id))} />
            <dl className="m-0 mt-3 grid grid-cols-2 gap-x-5 gap-y-2.5">
              <RowFact label="Raum" value={row.room_name || "—"} />
              <RowFact label="Menge" value={row.quantity} mono />
              {row.first_assigned_at ? (
                <RowFact label="Im Einsatz seit" value={formatDateOnly(row.first_assigned_at)} />
              ) : null}
              {row.first_assigned_at && usageDurationLabel ? (
                <RowFact label="Einsatzzeit" value={usageDurationLabel} />
              ) : null}
              {row.condition_at_assignment ? (
                <RowFact label="Zustand bei Zuweisung" value={row.condition_at_assignment} />
              ) : null}
            </dl>
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-2.5">
              <ActionButtons
                onReportDamage={() => handlers.onReportDamage(row)}
                onMove={() => handlers.onMove(row)}
                onEdit={() => handlers.onEdit(row)}
                onRemove={() => handlers.onRemove(row)}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function InventoryAssignmentsSection({
  assignments,
  available,
  error,
  unitById,
  usageDurationFor,
  onAdd,
  onReportDamage,
  onMove,
  onEdit,
  onRemove,
}) {
  const isMobile = useIsMobile();
  const View = isMobile ? AssignmentCards : AssignmentTable;
  return (
    <DetailCard
      title="Zuordnungen"
      action={
        <button
          type="button"
          onClick={onAdd}
          disabled={available <= 0}
          className="rounded-[8px] bg-primary px-3.5 py-2 text-[12.5px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          Zuordnung hinzufügen
        </button>
      }
    >
      {/* Kept exactly as it is: while a dialog is open the same message is
          printed here AND inside the dialog. S6a pins the pair (D27). */}
      {error ? <p className="m-0 mb-3 text-[13px] font-medium text-destructive">{error}</p> : null}

      {assignments.length === 0 ? (
        <p className="m-0 rounded-[8px] border border-dashed border-border bg-muted px-3 py-6 text-center text-[13px] font-medium text-muted-foreground">
          Keine Zuordnungen.
        </p>
      ) : (
        <View
          assignments={assignments}
          unitById={unitById}
          usageDurationFor={usageDurationFor}
          handlers={{ onReportDamage, onMove, onEdit, onRemove }}
        />
      )}
    </DetailCard>
  );
}
